/**
 * Source of Funds & Source of Wealth Questionnaire — v2 (MLRO sign-off 22 May 2026).
 *
 * Schema-driven wrapper around FormWizard.  The canonical schema lives
 * in backend/hub/sof_schema.py and is fetched at runtime from
 * /api/sof/schema — adding a new question on the backend lights up the
 * UI automatically.
 *
 * Section structure (15 sections, ~85 questions):
 *   §1  About you                         (always)
 *   §2  Personal savings                  (gated on §1.11 = Personal savings)
 *   §3  Third party gift                  (gated on §1.11 = Third party gift)
 *   §4  Mortgage loan                     (gated on §1.11 = Mortgage)
 *   §5  Sale of property                  (gated on §1.11 = Sale of property)
 *   §6  ISA / LISA / Help-to-Buy ISA      (gated on §1.11 = ISA …)
 *   §7  Divorce settlement                (gated on §1.11 = Divorce …)
 *   §8  Inheritance                       (gated on §1.11 = Inheritance)
 *   §9  Other source                      (gated on §1.11 = Other)
 *   §10 Funding allocation                (always — includes GA-8 abroad branch)
 *   §11 Source of Wealth (tiered 5+8)     (always)
 *   §12 Politically exposed persons       (always)
 *   §13 Sanctions / high-risk juris       (always)
 *   §14 Funding red flags (cash/crypto/3p) (always)
 *   §15 Declaration & signature           (always)
 *
 * References (grepable for the Node invariant harness):
 *   s1_applicable_sources  s1_capacity  s1_purpose
 *   s2_savings_uk_bank  s2_savings_underlying_origin  (GA-3 UK-bank branch)
 *   s10_purchase_price  s10_share_of_contribution  s10_from_abroad  (GA-8)
 *   s11_career_income … s11_gift_history  (5 SoW baseline per MLRO Q6)
 *   s12_pep_status  s13_sanctions_connection
 *   s14_cash  s14_cash_band  s14_crypto  s14_third_party_loan
 *   s15_privacy_notice_ack  s15_declaration_truthful  s15_declaration_ongoing
 *   declaration_confirmed  declaration_signed_by
 *
 * PDF endpoint: /api/sof/${token}/pdf
 *
 * ── AML Section 2 (R2) — SoF depth + gap rationale + evidence trail ──
 *
 * The fee-earner-facing SoFSection2 component below renders the
 * evidence-row + origin-row tables, the conditional gap-rationale
 * field, and the Med/High-only SoF rationale field. It POSTs to
 * /api/sof/depth/save (server-authoritative validation + append-only
 * sof_history snapshot).
 *
 * Closes SRA inspection findings on SL/SANG.002.88 and SL.SG.005.0002
 * per ref RGC-000183345 (21 May 2026). See
 * build_brief_aml_section_2_sof_depth.md.
 */
import React, { useMemo, useState } from "react";
import axios from "axios";
import { ShieldX, ShieldAlert, Plus, Trash2, Check, AlertTriangle, FolderOpen } from "lucide-react";
import FormWizard from "./TAFormWizard";
import SofFolderPicker from "../components/SofFolderPicker";
import { Button } from "../components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL || "";

// Rows added from the folder carry the SharePoint item id so Claude can read
// the document; it is panel state only and never saved.
const LOCAL_ROW_KEYS = ["_item_id"];
const forSave = row => Object.fromEntries(
    Object.entries(row).filter(([k]) => !LOCAL_ROW_KEYS.includes(k)));

export const SOF_EVIDENCE_TYPES = [
    "bank_statement",
    "payslip",
    "p60",
    "mortgage_offer",
    "completion_statement",
    "inheritance_grant",
    "gift_letter",
];

export const SOF_ORIGIN_TYPES = [
    "employment_income",
    "personal_savings",
    "mortgage",
    "property_sale",
    "isa_lisa_htb",
    "inheritance",
    "third_party_gift",
    "other",
];

export const SOF_GAP_RATIONALE_MIN_CHARS = 120;
export const SOF_RATIONALE_MIN_CHARS = 120;
export const SOF_ORIGIN_DETAILS_MIN_CHARS = 40;

// AML Section 3 (PR #546) — Company CDD + company-individual link.
// All five constants are in lock-step with backend/hub/sof_schema.py;
// any drift fails the backend snapshot dumper (PR #545) and surfaces
// in CI before this file ever ships.
export const COMPANY_CDD_DOCUMENT_TYPES = [
    "certificate_of_incorporation",
    "register_of_members",
    "register_of_directors",
    "psc_register",
    "memorandum_articles",
    "good_standing_certificate",
];

export const COMPANY_LINK_ROLES = [
    "director",
    "shareholder",
    "beneficial_owner",
    "psc",
    "other",
];

export const COMPANY_LINK_RATIONALE_MIN_CHARS = 60;

// Companies House regex — V's OQ2 ack, case-sensitive. Note `i` flag is
// deliberately ABSENT so 'ab123456' is rejected (matches backend
// behaviour; UX shows a hint rather than silently uppercasing).
export const COMPANIES_HOUSE_NUMBER_REGEX = /^([A-Z]{2}\d{6}|\d{8})$/;

const emptyCompanyCdd = () => ({
    company_name: "",
    company_number: "",
    documents: [],
    notes: "",
});

const emptyCompanyIndividualLink = () => ({
    link_evidenced: false,
    link_role: "director",
    link_role_other_text: "",
    link_rationale: "",
    link_doc_ref: "",
});

const emptyEvidenceRow = () => ({
    evidence_type: "bank_statement",
    evidence_amount_gbp: "",
    evidence_doc_ref: "",
    notes: "",
});

// Evidence type guessed from a document's file name, for rows added from
// the matter folder. A person can change it; unknown names fall back to a
// bank statement, the same default as a blank row.
const EVIDENCE_TYPE_BY_NAME = [
    [/p60/i, "p60"],
    [/pay ?slip/i, "payslip"],
    [/completion statement/i, "completion_statement"],
    [/mortgage|offer of loan|\baip\b|agreement in principle/i, "mortgage_offer"],
    [/probate|letters of administration|inheritance|estate/i, "inheritance_grant"],
    [/gift/i, "gift_letter"],
];

export function guessEvidenceType(fileName) {
    const hit = EVIDENCE_TYPE_BY_NAME.find(([re]) => re.test(fileName || ""));
    return hit ? hit[1] : "bank_statement";
}

const emptyOriginRow = () => ({
    origin_type: "personal_savings",
    origin_amount_gbp: "",
    origin_details: "",
    origin_type_other_text: "",
    // AML Section 3 (PR #546) — universal company toggle. Off by default
    // so every existing test/fixture continues to validate identically.
    origin_source_is_company: false,
    company_cdd: emptyCompanyCdd(),
    company_individual_link: emptyCompanyIndividualLink(),
});

/**
 * What Claude read on a folder document, under the row's amount. Amber until
 * a person presses Use; nothing counts towards the totals before then.
 */
function ReadingHint({ read, amountEntered, onUse, onRetry, testId }) {
    if (read.state === "reading") {
        return <span className="block text-[11px] text-slate-400 mt-0.5" data-testid={testId}>Claude is reading…</span>;
    }
    if (read.state === "failed" || read.amount_gbp == null) {
        return (
            <span className="block text-[11px] text-slate-400 mt-0.5 max-w-[14rem]" data-testid={testId}>
                {read.basis}{" "}
                {read.state === "failed" && <button type="button" onClick={onRetry} className="underline">Try again</button>}
            </span>
        );
    }
    if (amountEntered) return null;
    return (
        <span className="flex items-start gap-1 text-[11px] text-amber-300 mt-0.5 max-w-[14rem]" data-testid={testId}>
            <span>
                Claude read £{Number(read.amount_gbp).toLocaleString("en-GB", { minimumFractionDigits: 2 })}
                <span className="block text-slate-400">{read.basis}</span>
            </span>
            <button type="button" onClick={onUse}
                    className="shrink-0 px-1.5 rounded border border-amber-300/40 hover:bg-amber-300/10"
                    data-testid={`${testId}-use`}>
                Use
            </button>
        </span>
    );
}

/**
 * SoFSection2 — fee-earner-facing depth/gap/evidence panel.
 *
 * Renders OUTSIDE the client-facing FormWizard. Mounted by MatterDetail's
 * SoF Review panel (placeholder lighting up in a follow-up round) AND
 * directly importable here so the brief's required SourceOfFundsForm.jsx
 * mutation surfaces the data-testid contract for the Node harness.
 */
// 28 Oct 2026: plain words for the refusals a sign-off most often meets.
const SOF_SAVE_REFUSAL_LABELS = {
    purchase_price_missing: "Enter the purchase price at the top of the panel.",
    no_evidence_rows: "Add at least one piece of evidence of funds.",
    no_origin_rows: "Add at least one origin of funds row.",
    gap_rationale_too_short: "The evidence or origin falls short of the price: explain the shortfall in the gap rationale box (120 characters or more).",
};

export function SoFSection2({ matterReference, matterValueGbp, matterValueSource = "", priceRequired = true, matterRiskBand, onSaved, latest, maySign = false }) {
    // 28 Oct 2026: the price the gap rule measures against. Prefilled from the
    // matter, quote or client questionnaire; the fee earner can correct it.
    const [price, setPrice] = useState(() => {
        const v = latest?.matter_value_gbp ?? matterValueGbp;
        return v ? String(v) : "";
    });
    const priceGbp = parseFloat(String(price).replace(/[£,\s]/g, "")) || null;
    // Opens on the last save (gate status `latest`) when there is one, so a
    // second visit shows what was recorded rather than blank boxes.
    const [evidenceRows, setEvidenceRows] = useState(() =>
        latest?.evidence_rows?.length
            ? latest.evidence_rows.map(r => ({ ...emptyEvidenceRow(), ...r,
                evidence_doc_ref: r.evidence_doc_ref || "", notes: r.notes || "" }))
            : [emptyEvidenceRow()]);
    const [originRows, setOriginRows] = useState(() =>
        latest?.origin_rows?.length
            ? latest.origin_rows.map(r => ({ ...emptyOriginRow(), ...r,
                origin_type_other_text: r.origin_type_other_text || "",
                company_cdd: r.company_cdd || emptyCompanyCdd(),
                company_individual_link: r.company_individual_link || emptyCompanyIndividualLink() }))
            : [emptyOriginRow()]);
    const [gapRationale, setGapRationale] = useState(latest?.source_of_funds_gap_rationale || "");
    const [sofRationale, setSofRationale] = useState(latest?.source_of_funds_rationale || "");
    const [saving, setSaving] = useState(false);
    const [serverGate, setServerGate] = useState(null);
    // Vanessa, 26 Oct 2026: support staff prepare, the fee earner or a
    // director signs off; only a sign-off counts for the golden rule.
    const [draftNote, setDraftNote] = useState(
        latest?.status === "draft"
            ? { by: latest.prepared_by, at: latest.created_at, saved: false }
            : null);
    const [pickerOpen, setPickerOpen] = useState(false);

    const totals = useMemo(() => {
        const total_evidenced = evidenceRows.reduce(
            (acc, r) => acc + (parseFloat(r.evidence_amount_gbp || 0) || 0), 0,
        );
        const total_origin_attributed = originRows.reduce(
            (acc, r) => acc + (parseFloat(r.origin_amount_gbp || 0) || 0), 0,
        );
        return { total_evidenced, total_origin_attributed };
    }, [evidenceRows, originRows]);

    // Client-side mirror of the server-authoritative gap rule (server is
    // the source of truth — this is purely to drive conditional rendering
    // of the gap-rationale field).
    const gapPresent = useMemo(() => {
        if (priceGbp == null || priceGbp <= 0) return false;
        return (
            totals.total_evidenced < priceGbp ||
            totals.total_origin_attributed < priceGbp
        );
    }, [totals, priceGbp]);

    const isMedHigh = useMemo(() => {
        const b = (matterRiskBand || "").toString().toLowerCase();
        return b === "medium" || b === "high";
    }, [matterRiskBand]);

    const gapRationaleOk = (gapRationale || "").trim().length >= SOF_GAP_RATIONALE_MIN_CHARS;
    const sofRationaleOk = (sofRationale || "").trim().length >= SOF_RATIONALE_MIN_CHARS;

    const setEvidenceRow = (i, patch) => setEvidenceRows(rows =>
        rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
    const setOriginRow = (i, patch) => setOriginRows(rows =>
        rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

    // Rows from ticked folder documents, link and type filled in. A single
    // untouched blank row (the panel's starting state) is replaced.
    // Claude's reading of each folder document, by SharePoint item id:
    // { state: "reading" | "ready" | "failed", amount_gbp, basis, evidence_type }.
    // A suggestion only: the row's amount stays empty until a person presses Use.
    const [reads, setReads] = useState({});

    const readAmount = async (itemId) => {
        setReads(rs => ({ ...rs, [itemId]: { state: "reading" } }));
        try {
            const r = await axios.post(`${API}/api/sof-evidence/read-amount`, {
                matter_reference: matterReference, item_id: itemId,
            });
            const data = r?.data || {};
            setReads(rs => ({ ...rs, [itemId]: { state: "ready", ...data } }));
        } catch (e) {
            const detail = e?.response?.data?.detail;
            setReads(rs => ({ ...rs, [itemId]: { state: "failed",
                basis: typeof detail === "string" ? detail : "Could not read this document." } }));
        }
    };

    const useReading = (i, read) => setEvidenceRow(i, {
        evidence_amount_gbp: read.amount_gbp,
        ...(read.evidence_type ? { evidence_type: read.evidence_type } : {}),
        notes: [evidenceRows[i]?.notes, read.basis].filter(Boolean).join(" · "),
    });

    const addFromFolder = (docs) => {
        const added = docs.map(d => ({
            ...emptyEvidenceRow(),
            evidence_type: guessEvidenceType(d.name),
            evidence_doc_ref: d.web_url,
            notes: d.name,
            _item_id: d.id,
        }));
        docs.forEach(d => d.id && readAmount(d.id));
        setEvidenceRows(rs => {
            const blank = r => !r.evidence_doc_ref && !r.evidence_amount_gbp && !r.notes;
            return (rs.length === 1 && blank(rs[0])) ? added : [...rs, ...added];
        });
        setPickerOpen(false);
    };

    const onSave = async (asDraft = false) => {
        setSaving(true);
        try {
            const r = await axios.post(`${API}/api/sof/depth/save`, {
                matter_reference: matterReference,
                as_draft: asDraft,
                matter_value_gbp: priceGbp,
                evidence_rows: evidenceRows.map(row => ({
                    ...forSave(row),
                    evidence_amount_gbp: parseFloat(row.evidence_amount_gbp || 0) || 0,
                })),
                origin_rows: originRows.map(row => ({
                    ...row,
                    origin_amount_gbp: parseFloat(row.origin_amount_gbp || 0) || 0,
                })),
                source_of_funds_gap_rationale: gapRationale || null,
                source_of_funds_rationale: sofRationale || null,
            });
            if (r.data?.status === "draft") {
                setServerGate(null);
                setDraftNote({ by: null, at: r.data?.saved_at, saved: true });
                if (onSaved) onSaved(r.data?.gate || null);
                return;
            }
            setDraftNote(null);
            setServerGate(r.data?.gate || null);
            // BM-H3558: let the matter page refresh its SoF banner.
            if (onSaved) onSaved(r.data?.gate || null);
        } catch (e) {
            setServerGate({
                passed: false,
                blockers: e?.response?.data?.detail?.rows
                    ? Object.entries(e.response.data.detail.rows).map(([k, v]) => `${k}:${v.join(",")}`)
                    : (e?.response?.data?.detail?.fields || ["save_failed"]),
                summary_line: e?.response?.data?.detail?.error || "save_failed",
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <section className="bg-white/[0.02] border border-white/10 rounded-lg p-5"
                 data-testid="sof-section2-panel">
            <h2 className="text-base font-semibold text-white mb-3">
                Source of Funds — depth, evidence and rationale
            </h2>

            {/* ── Purchase price: what the evidence must cover ── */}
            <div className="mb-5 flex flex-wrap items-end gap-3" data-testid="sof-price-field">
                <label className="text-sm font-medium text-teal">
                    {priceRequired ? "Purchase price" : "Purchase price or premium (if any)"}
                    <input type="text" inputMode="decimal" value={price}
                           onChange={e => setPrice(e.target.value)}
                           placeholder="£"
                           data-testid="sof-price-input"
                           className="block mt-1 w-44 bg-transparent border border-white/20 rounded px-2 py-1 text-white" />
                </label>
                <p className="text-xs text-slate-400 pb-1.5" data-testid="sof-price-source">
                    {priceGbp
                        ? (matterValueSource && priceGbp === matterValueGbp
                            ? `From the ${matterValueSource}. Correct it here if it has changed.`
                            : "Recorded on the matter when you sign off.")
                        : (priceRequired
                            ? "Needed before sign-off: the evidence and origin totals are checked against it."
                            : "Leave blank if the tenant pays no premium.")}
                </p>
            </div>

            {/* ── Funds-available (evidence) rows ── */}
            <div className="mb-5">
                <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-teal" data-testid="sof-evidence-table-heading">
                        Funds available — evidence on file
                    </h3>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm"
                                data-testid="sof-add-from-folder"
                                onClick={() => setPickerOpen(o => !o)}>
                            <FolderOpen className="w-3.5 h-3.5 mr-1" /> Add from folder
                        </Button>
                        <Button variant="outline" size="sm"
                                data-testid="sof-add-evidence-row"
                                onClick={() => setEvidenceRows(rs => [...rs, emptyEvidenceRow()])}>
                            <Plus className="w-3.5 h-3.5 mr-1" /> Add evidence
                        </Button>
                    </div>
                </div>
                {pickerOpen && (
                    <SofFolderPicker matterRef={matterReference}
                                     existingUrls={evidenceRows.map(r => r.evidence_doc_ref)}
                                     onAdd={addFromFolder}
                                     onClose={() => setPickerOpen(false)} />
                )}
                <table className="w-full text-xs">
                    <thead>
                        <tr className="text-slate-400">
                            <th className="text-left py-1">Type</th>
                            <th className="text-left py-1">Amount (£)</th>
                            <th className="text-left py-1">SharePoint URL</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {evidenceRows.map((r, i) => {
                            const hasDocRef = !!(r.evidence_doc_ref || "").trim();
                            return (
                                <tr key={i} data-testid={`sof-evidence-row-${i}`}>
                                    <td className="py-1 pr-2">
                                        <select value={r.evidence_type}
                                                onChange={e => setEvidenceRow(i, { evidence_type: e.target.value })}
                                                data-testid={`sof-evidence-row-${i}-type`}
                                                className="bg-transparent border border-white/10 rounded px-1.5 py-1">
                                            {SOF_EVIDENCE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                        </select>
                                    </td>
                                    <td className="py-1 pr-2">
                                        <input type="number" min="0" step="0.01"
                                               value={r.evidence_amount_gbp}
                                               onChange={e => setEvidenceRow(i, { evidence_amount_gbp: e.target.value })}
                                               data-testid={`sof-evidence-row-${i}-amount`}
                                               className="bg-transparent border border-white/10 rounded px-1.5 py-1 w-28" />
                                        {r._item_id && reads[r._item_id] && (
                                            <ReadingHint read={reads[r._item_id]} amountEntered={!!r.evidence_amount_gbp}
                                                         testId={`sof-evidence-row-${i}-reading`}
                                                         onUse={() => useReading(i, reads[r._item_id])}
                                                         onRetry={() => readAmount(r._item_id)} />
                                        )}
                                    </td>
                                    <td className="py-1 pr-2">
                                        <span className="inline-flex items-center gap-1">
                                            {hasDocRef
                                                ? <Check className="w-3.5 h-3.5 text-emerald-400" data-testid={`sof-evidence-row-${i}-docref-ok`} />
                                                : <AlertTriangle className="w-3.5 h-3.5 text-status-red" data-testid={`sof-evidence-row-${i}-docref-missing`} />}
                                            <input type="text" placeholder="https://…sharepoint…"
                                                   value={r.evidence_doc_ref}
                                                   onChange={e => setEvidenceRow(i, { evidence_doc_ref: e.target.value })}
                                                   data-testid={`sof-evidence-row-${i}-docref`}
                                                   className="bg-transparent border border-white/10 rounded px-1.5 py-1 w-72" />
                                        </span>
                                        {r.notes && (
                                            <span className="block text-[11px] text-slate-500 truncate max-w-[20rem]"
                                                  title={r.notes}
                                                  data-testid={`sof-evidence-row-${i}-note`}>
                                                {r.notes}
                                            </span>
                                        )}
                                    </td>
                                    <td className="py-1">
                                        <button onClick={() => setEvidenceRows(rs => rs.filter((_, idx) => idx !== i))}
                                                data-testid={`sof-evidence-row-${i}-remove`}
                                                className="text-status-red">
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                <p className="text-xs text-slate-500 mt-1" data-testid="sof-evidence-total">
                    Total evidenced: £{totals.total_evidenced.toLocaleString()}
                </p>
            </div>

            {/* ── SoF-origin (origin) rows ── */}
            <div className="mb-5">
                <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-teal" data-testid="sof-origin-table-heading">
                        Source of funds — origin tranches
                    </h3>
                    <Button variant="outline" size="sm"
                            data-testid="sof-add-origin-row"
                            onClick={() => setOriginRows(rs => [...rs, emptyOriginRow()])}>
                        <Plus className="w-3.5 h-3.5 mr-1" /> Add origin
                    </Button>
                </div>
                <table className="w-full text-xs">
                    <thead>
                        <tr className="text-slate-400">
                            <th className="text-left py-1">Type</th>
                            <th className="text-left py-1">Amount (£)</th>
                            <th className="text-left py-1">Origin details (≥{SOF_ORIGIN_DETAILS_MIN_CHARS} chars)</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {originRows.map((r, i) => {
                            const chNumber = (r.company_cdd?.company_number || "").trim();
                            const chValid = chNumber !== "" && COMPANIES_HOUSE_NUMBER_REGEX.test(chNumber);
                            const linkRationaleLen = (r.company_individual_link?.link_rationale || "").trim().length;
                            const linkRationaleOk = linkRationaleLen >= COMPANY_LINK_RATIONALE_MIN_CHARS;
                            return (
                            <React.Fragment key={i}>
                            <tr data-testid={`sof-origin-row-${i}`}>
                                <td className="py-1 pr-2">
                                    <select value={r.origin_type}
                                            onChange={e => setOriginRow(i, { origin_type: e.target.value })}
                                            data-testid={`sof-origin-row-${i}-type`}
                                            className="bg-transparent border border-white/10 rounded px-1.5 py-1">
                                        {SOF_ORIGIN_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                    {r.origin_type === "other" && (
                                        <input type="text" placeholder="describe other source"
                                               value={r.origin_type_other_text}
                                               onChange={e => setOriginRow(i, { origin_type_other_text: e.target.value })}
                                               data-testid={`sof-origin-row-${i}-other-text`}
                                               className="mt-1 bg-transparent border border-white/10 rounded px-1.5 py-1 w-full" />
                                    )}
                                </td>
                                <td className="py-1 pr-2">
                                    <input type="number" min="0" step="0.01"
                                           value={r.origin_amount_gbp}
                                           onChange={e => setOriginRow(i, { origin_amount_gbp: e.target.value })}
                                           data-testid={`sof-origin-row-${i}-amount`}
                                           className="bg-transparent border border-white/10 rounded px-1.5 py-1 w-28" />
                                </td>
                                <td className="py-1 pr-2">
                                    <textarea rows="2" value={r.origin_details}
                                              onChange={e => setOriginRow(i, { origin_details: e.target.value })}
                                              data-testid={`sof-origin-row-${i}-details`}
                                              className="bg-transparent border border-white/10 rounded px-1.5 py-1 w-full" />
                                    {/* AML Section 3 — universal company toggle (OQ1 ack). */}
                                    <label className="mt-2 inline-flex items-center gap-2 text-xs text-slate-300"
                                           data-testid={`sof-origin-row-${i}-is-company-toggle-label`}>
                                        <input type="checkbox" checked={!!r.origin_source_is_company}
                                               onChange={e => setOriginRow(i, { origin_source_is_company: e.target.checked })}
                                               data-testid={`sof-origin-row-${i}-is-company-toggle`} />
                                        Funds sourced via a company (CDD + link evidence required)
                                    </label>
                                </td>
                                <td className="py-1">
                                    <button onClick={() => setOriginRows(rs => rs.filter((_, idx) => idx !== i))}
                                            data-testid={`sof-origin-row-${i}-remove`}
                                            className="text-status-red">
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </td>
                            </tr>
                            {/* Section 3 revealed sub-section — full-width row beneath the origin row. */}
                            {r.origin_source_is_company && (
                            <tr data-testid={`sof-origin-row-${i}-company-subsection`}>
                                <td colSpan={4} className="pl-6 py-2 bg-white/[0.02] border-l-2 border-amber-500/40">
                                    <h4 className="text-xs font-medium text-amber-300 mb-2">
                                        Company CDD & link to client (Section 3)
                                    </h4>
                                    <div className="flex flex-wrap gap-2 mb-2">
                                        <input type="text" placeholder="Company name"
                                               value={r.company_cdd?.company_name || ""}
                                               onChange={e => setOriginRow(i, {
                                                   company_cdd: { ...r.company_cdd, company_name: e.target.value }
                                               })}
                                               data-testid={`sof-origin-row-${i}-company-name`}
                                               className="bg-transparent border border-white/10 rounded px-1.5 py-1 w-64" />
                                        <input type="text" placeholder="Companies House no. (e.g. 12345678 or OC123456)"
                                               value={r.company_cdd?.company_number || ""}
                                               onChange={e => setOriginRow(i, {
                                                   company_cdd: { ...r.company_cdd, company_number: e.target.value }
                                               })}
                                               data-testid={`sof-origin-row-${i}-company-number`}
                                               className="bg-transparent border border-white/10 rounded px-1.5 py-1 w-72 font-mono" />
                                        {chNumber !== "" && (chValid
                                            ? <span data-testid={`sof-origin-row-${i}-company-number-ok`}
                                                    className="text-xs text-emerald-400 flex items-center gap-1">
                                                <Check className="w-3.5 h-3.5" /> valid format
                                              </span>
                                            : <span data-testid={`sof-origin-row-${i}-company-number-invalid`}
                                                    className="text-xs text-status-red flex items-center gap-1">
                                                <AlertTriangle className="w-3.5 h-3.5" /> invalid — must be 8 digits OR 2 letters + 6 digits (uppercase)
                                              </span>)}
                                        {chValid && (
                                            <a href={`https://find-and-update.company-information.service.gov.uk/company/${encodeURIComponent(chNumber)}`}
                                               target="_blank" rel="noreferrer"
                                               data-testid={`sof-origin-row-${i}-company-ch-link`}
                                               className="text-xs text-teal underline">
                                                Open in Companies House ↗
                                            </a>
                                        )}
                                    </div>
                                    <div className="mb-2">
                                        <label className="block text-xs text-slate-300 mb-1">
                                            CDD documents on file (multi-select):
                                        </label>
                                        <div className="flex flex-wrap gap-2"
                                             data-testid={`sof-origin-row-${i}-company-docs`}>
                                            {COMPANY_CDD_DOCUMENT_TYPES.map(doc_type => {
                                                const docs = r.company_cdd?.documents || [];
                                                const existing = docs.find(d => d.doc_type === doc_type);
                                                const isOn = !!existing;
                                                return (
                                                    <span key={doc_type}
                                                          data-testid={`sof-origin-row-${i}-doc-${doc_type}`}
                                                          className="inline-flex items-center gap-1.5 text-xs">
                                                        <input type="checkbox" checked={isOn}
                                                               onChange={e => {
                                                                   const nextDocs = e.target.checked
                                                                       ? [...docs, { doc_type, doc_ref: "" }]
                                                                       : docs.filter(d => d.doc_type !== doc_type);
                                                                   setOriginRow(i, {
                                                                       company_cdd: { ...r.company_cdd, documents: nextDocs }
                                                                   });
                                                               }}
                                                               data-testid={`sof-origin-row-${i}-doc-${doc_type}-toggle`} />
                                                        <span>{doc_type}</span>
                                                        {isOn && (
                                                            <input type="text" placeholder="https://…"
                                                                   value={existing.doc_ref || ""}
                                                                   onChange={e => {
                                                                       const nextDocs = docs.map(d =>
                                                                           d.doc_type === doc_type
                                                                               ? { ...d, doc_ref: e.target.value }
                                                                               : d);
                                                                       setOriginRow(i, {
                                                                           company_cdd: { ...r.company_cdd, documents: nextDocs }
                                                                       });
                                                                   }}
                                                                   data-testid={`sof-origin-row-${i}-doc-${doc_type}-docref`}
                                                                   className="bg-transparent border border-white/10 rounded px-1 py-0.5 w-56" />
                                                        )}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    {/* Link block */}
                                    <div className="mt-3 border-t border-white/10 pt-2">
                                        <label className="inline-flex items-center gap-2 text-xs text-slate-300">
                                            <input type="checkbox"
                                                   checked={!!r.company_individual_link?.link_evidenced}
                                                   onChange={e => setOriginRow(i, {
                                                       company_individual_link: {
                                                           ...r.company_individual_link,
                                                           link_evidenced: e.target.checked,
                                                       }
                                                   })}
                                                   data-testid={`sof-origin-row-${i}-link-evidenced`} />
                                            Link from client to company is evidenced on file
                                        </label>
                                        <div className="flex flex-wrap gap-2 mt-2 items-center">
                                            <select value={r.company_individual_link?.link_role || "director"}
                                                    onChange={e => setOriginRow(i, {
                                                        company_individual_link: {
                                                            ...r.company_individual_link,
                                                            link_role: e.target.value,
                                                        }
                                                    })}
                                                    data-testid={`sof-origin-row-${i}-link-role`}
                                                    className="bg-transparent border border-white/10 rounded px-1.5 py-1">
                                                {COMPANY_LINK_ROLES.map(t => <option key={t} value={t}>{t}</option>)}
                                            </select>
                                            {r.company_individual_link?.link_role === "other" && (
                                                <input type="text" placeholder="describe other role"
                                                       value={r.company_individual_link?.link_role_other_text || ""}
                                                       onChange={e => setOriginRow(i, {
                                                           company_individual_link: {
                                                               ...r.company_individual_link,
                                                               link_role_other_text: e.target.value,
                                                           }
                                                       })}
                                                       data-testid={`sof-origin-row-${i}-link-role-other-text`}
                                                       className="bg-transparent border border-white/10 rounded px-1.5 py-1 w-56" />
                                            )}
                                            <input type="text" placeholder="link doc-ref https://…"
                                                   value={r.company_individual_link?.link_doc_ref || ""}
                                                   onChange={e => setOriginRow(i, {
                                                       company_individual_link: {
                                                           ...r.company_individual_link,
                                                           link_doc_ref: e.target.value,
                                                       }
                                                   })}
                                                   data-testid={`sof-origin-row-${i}-link-doc-ref`}
                                                   className="bg-transparent border border-white/10 rounded px-1.5 py-1 w-72" />
                                        </div>
                                        <textarea rows="2"
                                                  placeholder={`Link rationale (≥${COMPANY_LINK_RATIONALE_MIN_CHARS} chars)`}
                                                  value={r.company_individual_link?.link_rationale || ""}
                                                  onChange={e => setOriginRow(i, {
                                                      company_individual_link: {
                                                          ...r.company_individual_link,
                                                          link_rationale: e.target.value,
                                                      }
                                                  })}
                                                  data-testid={`sof-origin-row-${i}-link-rationale`}
                                                  className="mt-2 bg-transparent border border-white/10 rounded px-1.5 py-1 w-full text-xs" />
                                        <p className="text-xs mt-1"
                                           data-testid={`sof-origin-row-${i}-link-rationale-counter`}>
                                            <span className={linkRationaleOk ? "text-emerald-400" : "text-status-red"}>
                                                {linkRationaleLen} / {COMPANY_LINK_RATIONALE_MIN_CHARS}
                                            </span>
                                        </p>
                                    </div>
                                </td>
                            </tr>
                            )}
                            </React.Fragment>
                        );})}
                    </tbody>
                </table>
                <p className="text-xs text-slate-500 mt-1" data-testid="sof-origin-total">
                    Total origin attributed: £{totals.total_origin_attributed.toLocaleString()}
                </p>
            </div>

            {/* ── Gap rationale — conditional on totals mismatch ── */}
            {gapPresent && (
                <div className="mb-4" data-testid="sof-gap-rationale-field">
                    <label className="block text-sm font-medium text-amber-300 mb-1">
                        Gap rationale (min {SOF_GAP_RATIONALE_MIN_CHARS} chars) — required because the evidence or origin totals are below the purchase price
                    </label>
                    <textarea rows="3" value={gapRationale}
                              onChange={e => setGapRationale(e.target.value)}
                              data-testid="sof-gap-rationale-textarea"
                              className="w-full bg-transparent border border-amber-500/30 rounded px-2 py-1" />
                    <p className="text-xs mt-1"
                       data-testid="sof-gap-rationale-counter">
                        <span className={gapRationaleOk ? "text-emerald-400" : "text-status-red"}>
                            {(gapRationale || "").trim().length} / {SOF_GAP_RATIONALE_MIN_CHARS}
                        </span>
                    </p>
                </div>
            )}

            {/* ── Med/High rationale — independent of gap rule ── */}
            {isMedHigh && (
                <div className="mb-4" data-testid="sof-rationale-field">
                    <label className="block text-sm font-medium text-amber-300 mb-1">
                        Source of Funds rationale (min {SOF_RATIONALE_MIN_CHARS} chars) — required for Medium/High risk matters
                    </label>
                    <textarea rows="3" value={sofRationale}
                              onChange={e => setSofRationale(e.target.value)}
                              data-testid="sof-rationale-textarea"
                              className="w-full bg-transparent border border-amber-500/30 rounded px-2 py-1" />
                    <p className="text-xs mt-1"
                       data-testid="sof-rationale-counter">
                        <span className={sofRationaleOk ? "text-emerald-400" : "text-status-red"}>
                            {(sofRationale || "").trim().length} / {SOF_RATIONALE_MIN_CHARS}
                        </span>
                    </p>
                </div>
            )}

            {draftNote && (
                <div className="mb-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-md text-xs text-amber-300"
                     data-testid="sof-section2-draft-note">
                    {draftNote.saved
                        ? "Draft saved. It does not count until the fee earner or a director signs it off."
                        : `Draft prepared${draftNote.by ? ` by ${draftNote.by}` : ""}${draftNote.at ? ` on ${new Date(draftNote.at).toLocaleDateString("en-GB")}` : ""}. Not yet signed off.`}
                </div>
            )}
            {!maySign && (
                <p className="text-xs text-slate-400 mb-2 text-right" data-testid="sof-section2-signer-note">
                    Only the matter's fee earner or a director can sign off the source of funds. Save your work as a draft for them to check.
                </p>
            )}
            <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => onSave(true)} disabled={saving}
                        data-testid="sof-section2-draft-button">
                    {saving ? "Saving…" : "Save draft"}
                </Button>
                {maySign && (
                    <Button onClick={() => onSave(false)} disabled={saving}
                            data-testid="sof-section2-save-button">
                        {saving ? "Saving…" : "Sign off source of funds"}
                    </Button>
                )}
            </div>

            {serverGate && !serverGate.passed && (
                <div className="mt-4 p-3 bg-status-red/10 border border-status-red/30 rounded-md"
                     data-testid="sof-section2-server-blockers">
                    <p className="text-xs text-status-red font-semibold flex items-center gap-1">
                        <ShieldX className="w-3.5 h-3.5" /> Not signed off yet:
                    </p>
                    <ul className="text-xs text-status-red/80 list-disc list-inside mt-1">
                        {(serverGate.blockers || []).map((b, i) => <li key={i}>{SOF_SAVE_REFUSAL_LABELS[b] || b}</li>)}
                    </ul>
                </div>
            )}
            {serverGate && serverGate.passed && (
                <div className="mt-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-md"
                     data-testid="sof-section2-server-passed">
                    <p className="text-xs text-emerald-400 flex items-center gap-1">
                        <ShieldAlert className="w-3.5 h-3.5" />
                        Source of funds signed off. The matter can now go to exchange.
                    </p>
                </div>
            )}
        </section>
    );
}

export default function SourceOfFundsForm() {
    return <FormWizard formType="sof" />;
}
